import type {
	ImportTransform,
	ImportTransformDescriptor,
	ImportTransformParamSpec,
	ImportTransformType,
} from '@today/types'
import { Button } from '@today/ui-primitives'
import { useState } from 'react'
import { formatLookup, newTransform, parseLookup, parseScalar } from './transform-params'

const INPUT_CLASS =
	'h-9 rounded-ds-md border border-ds-border bg-ds-surface px-2 text-ds-sm text-ds-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring'

type Catalogue = Readonly<Record<ImportTransformType, ImportTransformDescriptor>>

/**
 * Props for {@link TransformEditor}.
 *
 * @property label - What the transforms apply to (e.g. the canonical field); prefixes every control's name.
 * @property transforms - The transforms, in run order.
 * @property catalogue - The transform catalogue (`IMPORT_TRANSFORM_CATALOGUE`).
 * @property onChange - Called with the whole new list.
 */
export interface TransformEditorProps {
	label: string
	transforms: readonly ImportTransform[]
	catalogue: Catalogue
	onChange: (transforms: ImportTransform[]) => void
}

interface ParamInputProps {
	name: string
	spec: ImportTransformParamSpec
	value: unknown
	onChange: (value: unknown) => void
}

function NumberParam({ name, spec, value, onChange }: ParamInputProps) {
	const integer = spec.kind === 'integer' ? spec : null
	return (
		<input
			type="number"
			aria-label={name}
			className={INPUT_CLASS}
			value={value === null || value === undefined ? '' : String(value)}
			min={integer?.min}
			max={integer?.max}
			onChange={(e) => onChange(e.target.value === '' && integer?.nullable ? null : Number(e.target.value))}
		/>
	)
}

function ParamInput(props: ParamInputProps) {
	const { name, spec, value, onChange } = props
	if (spec.kind === 'integer' || spec.kind === 'number') return <NumberParam {...props} />
	if (spec.kind === 'enum') {
		return (
			<select
				aria-label={name}
				className={INPUT_CLASS}
				value={String(value)}
				onChange={(e) => onChange(e.target.value)}
			>
				{spec.values.map((v) => (
					<option key={v} value={v}>
						{v}
					</option>
				))}
			</select>
		)
	}
	if (spec.kind === 'lookup') {
		return (
			<textarea
				aria-label={name}
				className={INPUT_CLASS}
				rows={3}
				placeholder="from=to, one per line"
				defaultValue={formatLookup(value)}
				onChange={(e) => onChange(parseLookup(e.target.value))}
			/>
		)
	}
	return (
		<input
			aria-label={name}
			className={INPUT_CLASS}
			value={typeof value === 'boolean' ? String(value) : String(value ?? '')}
			onChange={(e) => onChange(spec.kind === 'scalar' ? parseScalar(e.target.value) : e.target.value)}
		/>
	)
}

interface TransformRowProps {
	label: string
	transform: ImportTransform
	descriptor: ImportTransformDescriptor
	onChange: (transform: ImportTransform) => void
	onRemove: () => void
}

function TransformRow({ label, transform, descriptor, onChange, onRemove }: TransformRowProps) {
	return (
		<li className="flex flex-wrap items-center gap-2">
			<span>{descriptor.label}</span>
			{descriptor.params.map((spec) => (
				<ParamInput
					key={spec.name}
					name={`${label} ${descriptor.label} ${spec.name}`}
					spec={spec}
					value={transform[spec.name]}
					onChange={(value) => onChange({ ...transform, [spec.name]: value })}
				/>
			))}
			<Button variant="muted" size="sm" aria-label={`Remove ${descriptor.label} from ${label}`} onPress={onRemove}>
				Remove
			</Button>
		</li>
	)
}

function TransformAdder({
	label,
	catalogue,
	onAdd,
}: {
	label: string
	catalogue: Catalogue
	onAdd: (t: ImportTransform) => void
}) {
	const types = Object.keys(catalogue) as ImportTransformType[]
	const [adding, setAdding] = useState<ImportTransformType>(types[0] as ImportTransformType)
	return (
		<div className="flex items-center gap-2">
			<select
				aria-label={`Transform to add to ${label}`}
				className={INPUT_CLASS}
				value={adding}
				onChange={(e) => setAdding(e.target.value as ImportTransformType)}
			>
				{types.map((type) => (
					<option key={type} value={type}>
						{catalogue[type].label}
					</option>
				))}
			</select>
			<Button
				variant="secondary"
				size="sm"
				aria-label={`Add transform to ${label}`}
				onPress={() => onAdd(newTransform(adding, catalogue[adding]))}
			>
				Add
			</Button>
		</div>
	)
}

/**
 * The transforms of one mapping item (TO-400 SCN-011): add one from the
 * catalogue, set its params, remove it. Inputs are rendered from the
 * catalogue's param specs, so a new param needs no new UI. Pure.
 */
export function TransformEditor({ label, transforms, catalogue, onChange }: TransformEditorProps) {
	return (
		<div className="flex flex-col gap-2">
			<ol className="flex flex-col gap-2">
				{transforms.map((transform, index) => (
					<TransformRow
						// biome-ignore lint/suspicious/noArrayIndexKey: transforms are ordered and may repeat
						key={`${index}:${transform.type}`}
						label={label}
						transform={transform}
						descriptor={catalogue[transform.type]}
						onChange={(next) => onChange(transforms.map((t, i) => (i === index ? next : t)))}
						onRemove={() => onChange(transforms.filter((_, i) => i !== index))}
					/>
				))}
			</ol>
			<TransformAdder label={label} catalogue={catalogue} onAdd={(t) => onChange([...transforms, t])} />
		</div>
	)
}
